/**
 * Eingeschränkte Textformatierung für Abstimmungstexte (§12).
 *
 * Unterstützt werden Absätze, Fettschrift, Kursivschrift, Aufzählungen, nummerierte Listen und
 * anklickbare Links. Nicht unterstützt werden Bilder, Videos, Dateianhänge, Tabellen, eingebettete
 * externe Inhalte, frei eingegebenes HTML, CSS, JavaScript und aktive Inhalte.
 *
 * **Die Sicherheitszusage steckt in der Bauweise, nicht in einem Filter.** {@link zerlegeText}
 * liefert einen typisierten Baum; die Oberfläche macht daraus React-Elemente. Es entsteht zu
 * keinem Zeitpunkt ein HTML-String, und `dangerouslySetInnerHTML` kommt nirgends vor. Alles, was
 * §12 verbietet, hat im Baum schlicht keine Entsprechung — ein Fehler im Zerleger ergibt darum
 * falsche Formatierung, niemals ausgeführten Code.
 *
 * Was der Zerleger nicht kennt, bleibt gewöhnlicher Text. `<script>` wird also angezeigt, nicht
 * ausgeführt — genau wie jedes andere Zeichen auch.
 */
/**
 * Zulässige Schemata für Links in **Abstimmungstexten** (§12).
 *
 * Ausschließlich `http` und `https`. `javascript:` und `data:` sind damit nicht bloß gefiltert,
 * sondern werden gar nicht erst als Link erkannt — sie bleiben gewöhnlicher Text.
 */
export const ERLAUBTE_SCHEMATA = ['http://', 'https://'];
/**
 * Zusätzlich zulässig in **Rechtstexten** — Impressum, Datenschutz und der Fußzeilenverweis
 * „Kontakt & Feedback" (ÄA-5).
 *
 * `mailto:` ist dort die wichtigste Linkart überhaupt: Ein Impressum ohne anklickbare
 * E-Mail-Adresse ist kaum eines. In Abstimmungstexten bleibt es bewusst außen vor — §12 zählt die
 * zulässigen Formatierungen abschließend auf, und eine Erweiterung dort wäre eine funktionale
 * Änderung ohne Anlass.
 *
 * Gefahrlos ist es, weil `mailto:` kein ausführbares Ziel ist: Der Browser übergibt die Adresse
 * dem Mailprogramm, er wertet nichts aus. `javascript:` und `data:` bleiben in **beiden** Listen
 * ausgeschlossen.
 */
export const ERLAUBTE_SCHEMATA_RECHTSTEXT = [...ERLAUBTE_SCHEMATA, 'mailto:'];
/** Aufzählungspunkt: `- ` oder `* ` am Zeilenanfang. */
const AUFZAEHLUNG = /^[-*]\s+(.*)$/;
/** Nummerierter Punkt: `1. ` am Zeilenanfang. */
const NUMMERIERT = /^\d+[.)]\s+(.*)$/;
/**
 * Ob ein Linkziel zulässig ist.
 *
 * Ohne zweites Argument gilt die engere Liste aus §12 — damit bleibt jeder bestehende Aufruf
 * unverändert, und eine Erweiterung muss man ausdrücklich verlangen.
 */
export function istErlaubtesZiel(ziel, schemata = ERLAUBTE_SCHEMATA) {
    const klein = ziel.toLowerCase();
    return schemata.some((schema) => klein.startsWith(schema));
}
/**
 * Zerlegt einen Abstimmungstext in Blöcke (§12).
 *
 * Absätze werden durch Leerzeilen getrennt. Aufeinanderfolgende Listenzeilen bilden eine Liste;
 * ein Wechsel zwischen Aufzählung und Nummerierung beginnt eine neue.
 */
export function zerlegeText(text) {
    const bloecke = [];
    const zeilen = text.replace(/\r\n?/g, '\n').split('\n');
    let absatz = [];
    let liste;
    function absatzAbschliessen() {
        if (absatz.length > 0) {
            bloecke.push({ art: 'absatz', teile: zerlegeTeile(absatz.join(' ')) });
            absatz = [];
        }
    }
    function listeAbschliessen() {
        if (liste !== undefined) {
            bloecke.push({
                art: 'liste',
                nummeriert: liste.nummeriert,
                punkte: liste.punkte.map((punkt) => zerlegeTeile(punkt)),
            });
            liste = undefined;
        }
    }
    for (const zeile of zeilen) {
        const inhalt = zeile.trim();
        if (inhalt === '') {
            absatzAbschliessen();
            listeAbschliessen();
            continue;
        }
        const aufzaehlung = AUFZAEHLUNG.exec(inhalt);
        const nummeriert = NUMMERIERT.exec(inhalt);
        const punkt = aufzaehlung ?? nummeriert;
        if (punkt !== null) {
            absatzAbschliessen();
            const istNummeriert = nummeriert !== null;
            if (liste !== undefined && liste.nummeriert !== istNummeriert) {
                listeAbschliessen();
            }
            liste ??= { nummeriert: istNummeriert, punkte: [] };
            liste.punkte.push(punkt[1] ?? '');
            continue;
        }
        listeAbschliessen();
        absatz.push(inhalt);
    }
    absatzAbschliessen();
    listeAbschliessen();
    return bloecke;
}
/**
 * Zerlegt eine Zeile in Textteile.
 *
 * Reihenfolge der Erkennung: Links zuerst, damit eine Adresse mit Sternchen nicht zerrissen wird;
 * danach Fett vor Kursiv, weil `**` sonst als zwei kursive Auszeichnungen gälte.
 *
 * **Wird auch von `richtext.ts` genutzt** (Rechtstexte nach ÄA-5). Fett, Kursiv und Link bedeuten
 * in einem Impressum dasselbe wie in einem Abstimmungstext; zwei Zerleger dafür wären zwei
 * Gelegenheiten, dass sie auseinanderlaufen. Der einzige Unterschied sind die zulässigen
 * Linkschemata — deshalb `schemata` als Argument statt einer zweiten Fassung.
 *
 * Die Liste wird bei jedem rekursiven Aufruf **durchgereicht**: Ohne das gälte `mailto:` in
 * `**[E-Mail](mailto:…)**` nicht mehr, weil die innere Zerlegung wieder bei §12 landete.
 */
export function zerlegeTeile(zeile, schemata = ERLAUBTE_SCHEMATA) {
    const teile = [];
    let puffer = '';
    function pufferLeeren() {
        if (puffer !== '') {
            teile.push({ art: 'text', text: puffer });
            puffer = '';
        }
    }
    let i = 0;
    while (i < zeile.length) {
        const rest = zeile.slice(i);
        // [Beschriftung](https://…)
        const markdownLink = /^\[([^\]]*)\]\(([^)\s]+)\)/.exec(rest);
        if (markdownLink !== null) {
            const [ganz, beschriftung = '', ziel = ''] = markdownLink;
            if (istErlaubtesZiel(ziel, schemata)) {
                pufferLeeren();
                teile.push({
                    art: 'link',
                    ziel,
                    teile: beschriftung === ''
                        ? [{ art: 'text', text: ziel }]
                        : zerlegeTeile(beschriftung, schemata),
                });
                i += ganz.length;
                continue;
            }
            // Unzulässiges Schema: bleibt gewöhnlicher Text.
            puffer += ganz;
            i += ganz.length;
            continue;
        }
        // Nackte Adresse — nur am Anfang oder nach einem Trennzeichen. Sonst würde auch eine Adresse
        // in einer HTML-Zeichenfolge wie `src="https://…"` als Link gelten, obwohl die umgebende
        // Auszeichnung nach §12 gerade gewöhnlicher Text bleiben soll.
        const davor = i === 0 ? '' : (zeile[i - 1] ?? '');
        const amWortanfang = davor === '' || /[\s(]/.test(davor);
        const nackt = amWortanfang ? /^https?:\/\/[^\s<>()"']+/i.exec(rest) : null;
        if (nackt !== null) {
            const ziel = nackt[0];
            pufferLeeren();
            teile.push({ art: 'link', ziel, teile: [{ art: 'text', text: ziel }] });
            i += ziel.length;
            continue;
        }
        const fett = /^\*\*(.+?)\*\*/.exec(rest);
        if (fett !== null) {
            pufferLeeren();
            teile.push({ art: 'fett', teile: zerlegeTeile(fett[1] ?? '', schemata) });
            i += fett[0].length;
            continue;
        }
        const kursiv = /^\*(.+?)\*/.exec(rest);
        if (kursiv !== null) {
            pufferLeeren();
            teile.push({ art: 'kursiv', teile: zerlegeTeile(kursiv[1] ?? '', schemata) });
            i += kursiv[0].length;
            continue;
        }
        puffer += zeile[i];
        i += 1;
    }
    pufferLeeren();
    return teile;
}
/**
 * Normalisiert einen Abstimmungstext (§12: „bereinigt Eingaben serverseitig").
 *
 * Zerlegt den Text und setzt ihn aus dem Baum wieder zusammen. Der gespeicherte Text ist damit
 * stets einer, der genau so wieder gelesen wird — was der Zerleger nicht kennt, überlebt als
 * gewöhnlicher Text und kann später nicht plötzlich als Auszeichnung gelten.
 */
export function normalisiereText(text) {
    return zerlegeText(text).map(baueBlock).join('\n\n');
}
function baueBlock(block) {
    if (block.art === 'absatz') {
        return baueTeile(block.teile);
    }
    return block.punkte
        .map((punkt, index) => `${block.nummeriert ? `${index + 1}.` : '-'} ${baueTeile(punkt)}`)
        .join('\n');
}
/** Setzt Textteile wieder zu Markdown zusammen. Auch von `richtext.ts` genutzt (ÄA-5). */
export function baueTeile(teile) {
    return teile
        .map((teil) => {
        switch (teil.art) {
            case 'text':
                return teil.text;
            case 'fett':
                return `**${baueTeile(teil.teile)}**`;
            case 'kursiv':
                return `*${baueTeile(teil.teile)}*`;
            case 'link': {
                const beschriftung = baueTeile(teil.teile);
                return beschriftung === teil.ziel ? teil.ziel : `[${beschriftung}](${teil.ziel})`;
            }
        }
    })
        .join('');
}
/** Der reine Text ohne jede Auszeichnung — für Kurzfassungen und spätere Exporte (§27). */
export function nurText(text) {
    return zerlegeText(text)
        .map((block) => block.art === 'absatz'
        ? teileAlsText(block.teile)
        : block.punkte.map((punkt) => teileAlsText(punkt)).join('\n'))
        .join('\n\n');
}
/** Der reine Text einer Teilfolge, ohne jede Auszeichnung. Auch von `richtext.ts` genutzt. */
export function teileAlsText(teile) {
    return teile.map((teil) => (teil.art === 'text' ? teil.text : teileAlsText(teil.teile))).join('');
}
//# sourceMappingURL=textformat.js.map